import React, { useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { Alert, Box, Button, Chip, Stack, Switch } from "@mui/material";
import { useSelector } from "react-redux";
import { defaultAllSelection } from "services/constants";
import { requestPayInputConfig } from "./payInputConfigApi";
import { requestReimbursementSettings } from "pages/PayRoll/Shared/reimbursementApi";
import { requestBonusSettings } from "pages/PayRoll/Shared/bonusApi";
import { designColors, APP_BAR_HEIGHT } from "services/constants/designTokens";
import {
  EventRepeatOutlined,
  HowToRegOutlined,
  StorageOutlined,
  CalendarMonthOutlined,
  AdjustOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import {
  ConfigPageShell,
  ConfigSettingCard,
  ConfigSelectField,
  ConfigRadioGroup,
  ConfigCheckboxList,
} from "pages/PayRoll/Shared/ConfigurationUi";
import {
  PAY_INPUT_CONFIG_ITEMS,
  DEFAULT_PAY_INPUT_CONFIG_KEY,
  APPROVAL_OPTIONS,
  approvalHelperText,
  validatePayInputConfig,
  isSameConfig,
} from "./payInputConfigData";
import { ReimbursementTypesCard } from "./ReimbursementTypes";
import { BonusTypesCard } from "./BonusTypes";
import { usePayrollSettingsCrumbs } from "../payrollSettingsMenu";
import { OpenDailyScreenButton } from "pages/PayRoll/Shared/settingsSwitch";

// Reached from Payroll > Configuration > Pay Input in the sidebar, which
// lists every pay input under it (routes.js builds those rows from
// PAY_INPUT_CONFIG_ITEMS). The page used to show the same list as its own
// navigation column. Each pay input has its own URL:
// /admin/payroll/configuration/pay-input-configuration/<pay input>.
export const PAY_INPUT_CONFIG_BASE_PATH = "/admin/payroll/configuration/pay-input-configuration";
const BASE_PATH = PAY_INPUT_CONFIG_BASE_PATH;

// Pay inputs with an "Enable" switch (is_enabled). When off, the screen is
// hidden from the Payroll > Pay Inputs flyout (Sidebar CONFIG_GATED_FLYOUT_ITEMS)
// and its URL redirects to the next Pay Inputs screen.
const ENABLE_SWITCHES = {
  "shift-earnings": {
    title: "Enable Shift Earnings",
    noun: "shift earnings",
    sources: "added manually, imported or received from external systems (ERP/Attendance)",
  },
  overtime: {
    title: "Enable Overtime Earnings",
    noun: "overtime earnings",
    sources: "added manually, imported or created from attendance (approved overtime)",
  },
  leave: {
    title: "Enable Leave Earnings",
    noun: "leave earnings",
    sources: "added manually, imported or created from attendance (approved paid leave)",
  },
  penalties: {
    title: "Enable Penalties",
    noun: "penalties",
    sources: "added manually, imported or created from attendance (late entry, early exit, break and absent)",
  },
  "loans-and-advances": {
    title: "Enable Loans & Advances",
    noun: "loans and advances",
    sources: "added manually or imported",
  },
  // Saved as is_enabled on the reimbursement settings API. When off,
  // employees and team leads can't submit new claims (My Workspace and
  // Team Workspace New Request); existing claims stay as they are.
  reimbursements: {
    title: "Enable Reimbursements",
    noun: "reimbursements",
    sources: "claimed by employees from My Workspace and raised by team leads from Team Workspace",
    disabledText: "When disabled, no new reimbursement claims can be submitted. Existing claims are not changed.",
  },
};

const getItemKey = (pathname) => {
  let path = pathname;
  while (path.endsWith("/")) path = path.slice(0, -1);
  if (!path.startsWith(`${BASE_PATH}/`)) return "";
  return path.slice(BASE_PATH.length + 1).split("/")[0].toLowerCase();
};

export const ConfigPayInputConfiguration = () => {
  const { pathname } = useLocation();
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const itemKey = getItemKey(pathname);
  const item = PAY_INPUT_CONFIG_ITEMS.find((entry) => entry.key === itemKey);
  if (!item) return <Redirect to={`${BASE_PATH}/${DEFAULT_PAY_INPUT_CONFIG_KEY}`} />;
  return <PayInputConfigurationScreen key={`${domain}:${topbarCompanyId}:${item.key}`} item={item} domain={domain} companyId={topbarCompanyId} />;
};

const PayInputConfigurationScreen = ({ item, domain, companyId }) => {
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const history = useHistory();
  const isShiftEarnings = item.key === "shift-earnings";
  const isReimbursements = item.key === "reimbursements";
  const isBonus = item.key === "bonus";
  const enableSwitch = ENABLE_SWITCHES[item.key];
  // Reimbursements and Bonus have their own settings APIs (settings/); the
  // rest share the pay-input configuration API.
  const requestConfig = isReimbursements
    ? requestReimbursementSettings
    : isBonus ? requestBonusSettings : requestPayInputConfig;
  const validCompany = Number(companyId) > 0 && Number(companyId) !== defaultAllSelection;
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");
  const [retry, setRetry] = useState(0);
  const saveControllerRef = useRef(null);
  const savingRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    if (!validCompany || !domain) {
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    setApiError("");
    requestConfig({ domain, companyId, payInputType: item.apiType, signal: controller.signal })
      .then(({ config }) => { if (!controller.signal.aborted) setSaved(config); })
      .catch((error) => { if (!controller.signal.aborted) setApiError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [domain, companyId, item.apiType, validCompany, retry, requestConfig]);

  useEffect(() => () => saveControllerRef.current?.abort(), []);
  // Unsaved edits, tagged with the pay input they belong to so switching
  // screens never shows another pay input's edits.
  const [edits, setEdits] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);

  const sourceOptions = saved?.sourceOptions || [];
  const draft = edits?.key === item.key ? edits.config : saved;
  const dirty = Boolean(saved && !isSameConfig(draft, saved));

  useEffect(() => {
    if (!dirty && !saving) return undefined;
    const unblock = history.block((nextLocation, action) => {
      if (!savingRef.current) setPendingNavigation({ location: nextLocation, action });
      return false;
    });
    unblockRef.current = unblock;
    return () => {
      unblock();
      unblockRef.current = null;
    };
  }, [dirty, saving, history]);

  // Switched off: the other settings are hidden, so they aren't checked
  // either (an empty source list must not block saving "Disabled").
  const detailsHidden = Boolean(enableSwitch && draft && !draft.enabled);
  const errors = draft && !detailsHidden ? validatePayInputConfig(draft) : {};
  const hasErrors = Object.keys(errors).length > 0;

  const updateDraft = (changes) =>
    setEdits({ key: item.key, config: { ...draft, ...changes } });

  const toggleSource = (value) =>
    updateDraft({
      sources: draft.sources.includes(value)
        ? draft.sources.filter((source) => source !== value)
        : [...draft.sources, value],
    });

  const handleSave = async () => {
    if (!draft || hasErrors || savingRef.current || !validCompany) return;
    savingRef.current = true;
    setSaving(true);
    setApiError("");
    const controller = new AbortController();
    saveControllerRef.current = controller;
    try {
      const result = await requestConfig({ domain, companyId, payInputType: item.apiType, config: draft, signal: controller.signal });
      if (controller.signal.aborted) return;
      setSaved(result.config);
      setEdits(null);
      showToast("success", result.message || `${item.label} configuration saved.`);
    } catch (error) {
      if (!controller.signal.aborted) setApiError(error.message);
    } finally {
      if (!controller.signal.aborted) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  return (
    <Box sx={{
      minHeight: `calc(100vh - ${APP_BAR_HEIGHT + 1}px)`,
      backgroundColor: designColors.pageBg,
    }}>
      <Box sx={{ minWidth: 0 }}>
        <ConfigPageShell
          breadcrumb={settingsCrumbs("Pay Input", item.label)}
          headerAction={<OpenDailyScreenButton group="pay-input" item={item.key} />}
          title={isShiftEarnings ? "Shift Earnings Configuration" : item.label}
          subtitle={isShiftEarnings
            ? "Configure how shift earnings are captured, approved and included in payroll."
            : isReimbursements
              ? "Configure the default behaviour, approval settings, allowed sources and reimbursement types."
              : isBonus
                ? "Pay Inputs · default behaviour, sources, rounding and bonus types."
                : `Configure the default behaviour, approval settings and allowed sources for ${item.label}.`}
          onCancel={() => { setEdits(null); setApiError(""); }}
          cancelDisabled={!dirty || saving}
          onSave={handleSave}
          saveDisabled={!dirty || hasErrors || !saved || loading || saving}
          saving={saving}
        >
          {!validCompany ? <Alert severity="info">Select a single company to configure pay inputs.</Alert> : loading ? (
            <Box role="status" aria-label="Loading configuration"><Loader info="Loading..." minHeight="40vh" /></Box>
          ) : !saved ? (
            <Alert severity="error" action={<Button color="inherit" onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>{apiError || "Unable to load configuration."}</Alert>
          ) : <>
            {apiError ? <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert> : null}
            <Box component="fieldset" disabled={saving} sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
            <Stack spacing={2}>
              {enableSwitch ? (
                <ConfigSettingCard
                  icon={CalendarMonthOutlined}
                  tint="blue"
                  title={enableSwitch.title}
                  description={`Control whether ${enableSwitch.noun} are available in the system.`}
                  footer={
                    <Alert severity="info" sx={{ borderRadius: 1 }}>
                      When enabled, {enableSwitch.noun} can be {enableSwitch.sources}.
                      {enableSwitch.disabledText || <>When disabled, {item.label} is hidden from Payroll &gt; Pay Inputs and no new records can be added.</>}
                    </Alert>
                  }
                >
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    <Switch
                      checked={draft.enabled}
                      onChange={(event) => updateDraft({ enabled: event.target.checked })}
                      inputProps={{ "aria-label": enableSwitch.title }}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: designColors.teal },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: designColors.teal },
                      }}
                    />
                    <Chip label={draft.enabled ? "Enabled" : "Disabled"} color={draft.enabled ? "success" : "default"} size="small" variant="outlined" />
                  </Stack>
                </ConfigSettingCard>
              ) : null}
              {detailsHidden ? null : <>
              <ConfigSettingCard
                icon={EventRepeatOutlined}
                tint="blue"
                title={isShiftEarnings ? "Default Payroll Cycle Treatment" : "Default Payroll Treatment"}
                description={isShiftEarnings
                  ? "Select the default payroll cycle for shift earnings records."
                  : isBonus
                    ? "Select the default payroll treatment for Bonus. This can be overridden in each bonus run or entry."
                    : `Select the default payroll treatment for ${item.label}. This can be overridden when adding or editing individual entries.`}
              >
                <ConfigSelectField
                  id="pay-input-default-treatment"
                  label="Default Treatment"
                  value={draft.treatment}
                  options={draft.treatmentOptions}
                  onChange={(treatment) => updateDraft({ treatment })}
                  helperText={isShiftEarnings ? "This can be overridden when adding or editing individual records." : undefined}
                />
              </ConfigSettingCard>

              {/* Bonus: no approval in V1 (spec B2), so No is fixed and Yes is disabled. */}
              {draft.approvalLocked ? <ConfigSettingCard
                icon={HowToRegOutlined}
                tint="green"
                title="Approval"
                description={`Specify whether ${item.label} entries require approval before they are included in payroll.`}
              >
                <ConfigRadioGroup
                  id="pay-input-approval"
                  label="Approval Required"
                  value="no"
                  options={APPROVAL_OPTIONS.map((option) => ({ ...option, disabled: option.value === "yes" }))}
                  onChange={() => {}}
                  helperText="Currently set to No. Bonus runs go to payroll when you click Add to payroll."
                />
              </ConfigSettingCard> : null}

              {draft.approvalConfigurable ? <ConfigSettingCard
                icon={HowToRegOutlined}
                tint="green"
                title="Approval"
                description={isShiftEarnings
                  ? "Specify whether shift earnings entries require approval before they are included in payroll."
                  : `Specify whether ${item.label} entries require approval before they are included in payroll.`}
              >
                <ConfigRadioGroup
                  id="pay-input-approval"
                  label="Approval Required"
                  value={draft.approvalRequired}
                  options={APPROVAL_OPTIONS}
                  onChange={(approvalRequired) => updateDraft({ approvalRequired })}
                  helperText={isShiftEarnings
                    ? draft.approvalRequired === "yes"
                      ? "Shift earnings records must be approved before they can be included in payroll."
                      : "Shift earnings records can be included in payroll without approval."
                    : approvalHelperText(item.label, draft.approvalRequired)}
                />
              </ConfigSettingCard> : null}

              <ConfigSettingCard
                icon={StorageOutlined}
                tint="purple"
                title="Allowed Sources"
                description={isShiftEarnings
                  ? "Select the sources from which shift earnings records can be created in the system."
                  : `Select the sources from which ${item.label} can be created in the system.`}
              >
                <ConfigCheckboxList
                  values={draft.sources}
                  options={sourceOptions}
                  onToggle={toggleSource}
                  error={errors.sources}
                />
              </ConfigSettingCard>
              {draft.roundingOptions?.length ? (
                <ConfigSettingCard
                  icon={AdjustOutlined}
                  tint="amber"
                  title="Rounding"
                  description="How bonus amounts are rounded for each employee. Rounding is done once, on the total."
                >
                  <ConfigSelectField
                    id="pay-input-rounding"
                    label="Round each employee's bonus to"
                    value={draft.rounding}
                    options={draft.roundingOptions}
                    onChange={(rounding) => updateDraft({ rounding })}
                    helperText="Nearest rupee rounds half up (₹100.50 → ₹101)."
                  />
                </ConfigSettingCard>
              ) : null}
              {isReimbursements ? <ReimbursementTypesCard domain={domain} companyId={companyId} /> : null}
              {isBonus ? <BonusTypesCard domain={domain} companyId={companyId} /> : null}
              </>}
            </Stack>
            </Box>
          </>}

          <GlobalConfirmDialog
            open={Boolean(pendingNavigation)}
            title="Discard unsaved changes?"
            confirmLabel="Discard"
            cancelLabel="Keep Editing"
            onCancel={() => setPendingNavigation(null)}
            onConfirm={() => {
              const navigation = pendingNavigation;
              if (!navigation) return;
              unblockRef.current?.();
              unblockRef.current = null;
              setPendingNavigation(null);
              setEdits(null);
              if (navigation.action === "REPLACE") {
                history.replace(navigation.location);
              } else {
                history.push(navigation.location);
              }
            }}
          >
            Your changes to {item.label} have not been saved.
          </GlobalConfirmDialog>
        </ConfigPageShell>
      </Box>
    </Box>
  );
};
